import React, {useId, useState} from 'react';
import {Box, Button} from '@mui/material';
import {ExpandLessRounded, ExpandMoreRounded} from '@mui/icons-material';
import classes from './ReceiptReviewWarnings.module.scss';

const warningMessages: Record<string, string> = {
  LAYOUT_EXTRACTION_REQUIRES_REVIEW: 'Check the detected items against the receipt.',
  MODEL_EXTRACTION_REQUIRES_REVIEW: 'Check the detected items against the receipt.',
  QUANTITY_UNKNOWN: 'Some item quantities could not be read. Check the quantities.',
  QUANTITY_NOT_EXPLICIT: 'Some item quantities were not explicit. Check the quantities.',
  CURRENCY_NOT_FOUND: 'Currency was not detected. Confirm it before saving.',
  LOW_CONFIDENCE_TEXT_PRESENT: 'Some receipt text was unclear. Check the detected details.',
  ITEM_ARITHMETIC_MISMATCH: 'Some line totals do not match quantity times unit price. Check the amounts.',
  ITEM_SUM_DIFFERS_FROM_TOTAL: 'The items do not add up to the scanned total. Check the amounts.',
  LINE_TOTAL_UNKNOWN: 'Some item totals could not be read. Check the amounts.',
  TOTAL_NOT_FOUND: 'The receipt total could not be read.',
  NO_ITEMS_FOUND: 'No items could be read from the receipt.',
  INVALID_TAX_RATE: 'Some tax rates could not be read correctly. Check the receipt.',
  INVALID_DATE: 'The receipt date could not be read correctly.',
  INVALID_CURRENCY: 'The detected currency was not recognized. Check the receipt currency.',
  MULTIPLE_CURRENCIES: 'More than one currency was detected. Check the receipt currency.',
  AMBIGUOUS_CURRENCY_SYMBOL: 'The currency symbol was unclear. Check the receipt currency.',
  MULTIPLE_TOTALS: 'More than one receipt total was detected. Check the amounts.',
  DISCOUNT_REQUIRES_REVIEW: 'A discount needs review. Check that the amounts include it correctly.',
  STORE_NOT_FOUND: 'The store name could not be read.',
  THOUSANDS_SEPARATOR_INFERRED_FROM_RECEIPT: 'Number separators were inferred from the receipt. Check the amounts.',
};

function friendlyWarning(warning: string): string {
  const code = /^([A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+)(?::\d+)?$/.exec(warning)?.[1];
  if (!code) return warning;
  return warningMessages[code] ?? code.charAt(0) + code.slice(1).toLowerCase().replace(/_/g, ' ');
}

const ReceiptReviewWarnings = ({warnings}: {warnings: string[]}) => {
  const [expanded, setExpanded] = useState(false);
  const [technicalExpanded, setTechnicalExpanded] = useState(false);
  const id = useId();
  const originals = Array.from(new Set(warnings.map(warning => warning.trim()).filter(Boolean)));
  const notes = Array.from(new Set(originals.map(friendlyWarning)));

  if (!originals.length) return null;

  return (
    <Box className={classes.card} sx={{borderColor: '#fcd34d', bgcolor: '#fffbeb', color: '#92400e'}}>
      <Button
        type="button"
        color="inherit"
        fullWidth
        className={classes.summary}
        aria-expanded={expanded}
        aria-controls={`${id}-notes`}
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-description`}
        onClick={() => {
          setExpanded(!expanded);
          setTechnicalExpanded(false);
        }}
        endIcon={expanded ? <ExpandLessRounded/> : <ExpandMoreRounded/>}
      >
        <span className={classes.summaryText}>
          <span id={`${id}-title`} className={classes.title}>Review scan notes</span>
          <span id={`${id}-description`} className={classes.description}>
            Some details may need a quick check.
          </span>
        </span>
      </Button>
      {expanded && (
        <div
          id={`${id}-notes`}
          role="region"
          aria-labelledby={`${id}-title`}
          tabIndex={0}
          className={classes.notes}
        >
          <ul className={classes.list}>
            {notes.map(note => <li key={note}>{note}</li>)}
          </ul>
          <Button
            type="button"
            color="inherit"
            className={classes.technicalToggle}
            aria-expanded={technicalExpanded}
            aria-controls={`${id}-technical`}
            onClick={() => setTechnicalExpanded(!technicalExpanded)}
            endIcon={technicalExpanded ? <ExpandLessRounded/> : <ExpandMoreRounded/>}
          >
            Technical details
          </Button>
          {technicalExpanded && (
            <ul id={`${id}-technical`} aria-label="Original scan notes" className={classes.list}>
              {originals.map(warning => <li key={warning}>{warning}</li>)}
            </ul>
          )}
        </div>
      )}
    </Box>
  );
};

export default ReceiptReviewWarnings;
